/**
 * P17 #51 / N67 — quote list/detail color helpers.
 *   - keyAccountClass: V1 formatview_quote.pl:294-313 (9-class key-account map)
 *   - daysOutTier:     V1 funcs_quote.pl:732-803 (expiry-urgency tiers)
 */

const KEY_ACCOUNT_CLASS: Record<string, string> = {
  'CONTRACT':       'bg-lime-100 text-lime-800 border border-lime-300 dark:bg-lime-900/30 dark:text-lime-300 dark:border-lime-700',
  'HYPER FOCUS':    'bg-sky-100 text-sky-800 border border-sky-300 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-700',
  'KEY ACCOUNTS 1': 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
  'KEY ACCOUNTS 2': 'bg-orange-100 text-orange-800 border border-orange-300 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700',
  'KEY ACCOUNTS 3': 'bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
  'KEY ACCOUNTS 4': 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
  'KEY PROSPECT':   'bg-purple-100 text-purple-800 border border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700',
};
// KEY PROSPECT 1–4 all share the purple class (V1).
for (const k of ['KEY PROSPECT 1', 'KEY PROSPECT 2', 'KEY PROSPECT 3', 'KEY PROSPECT 4']) {
  KEY_ACCOUNT_CLASS[k] = KEY_ACCOUNT_CLASS['KEY PROSPECT'];
}

/** Background/border class for a key-account value, or '' when unmapped/blank. */
export function keyAccountClass(value: string | null | undefined): string {
  return KEY_ACCOUNT_CLASS[(value ?? '').trim().toUpperCase()] ?? '';
}

/**
 * Every customer key status (cms.customers.keyaccount), in tier order — the
 * V1 fixed set. AVERAGE is the unmarked default most customers carry; it is
 * offered in filters but never drawn as a badge.
 */
export const KEY_ACCOUNT_STATUSES: readonly string[] = [
  'KEY ACCOUNTS 1',
  'KEY ACCOUNTS 2',
  'KEY ACCOUNTS 3',
  'KEY ACCOUNTS 4',
  'HYPER FOCUS',
  'CONTRACT',
  'KEY PROSPECT 1',
  'KEY PROSPECT 2',
  'KEY PROSPECT 3',
  'KEY PROSPECT 4',
  'AVERAGE',
];

/** Multi-select options for a Customer Key Status filter. */
export const KEY_STATUS_OPTIONS: { value: string; label: string }[] =
  KEY_ACCOUNT_STATUSES.map((s) => ({ value: s, label: s }));

/** True for the statuses worth flagging on a customer (everything but the
 *  AVERAGE default and blank). */
export function isFlaggedKeyStatus(value: string | null | undefined): boolean {
  const v = (value ?? '').trim().toUpperCase();
  return v !== '' && v !== 'AVERAGE';
}

export type DaysOutTier = 'red' | 'orange' | 'yellow' | null;

/** Parse an MM/DD/YYYY date (the formatted `expires`/enddate); null otherwise. */
function parseUsDate(s: string): Date | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec((s ?? '').trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Business days (Mon–Fri) strictly after `from`, up to and including `target`. */
function businessDaysUntil(target: Date, from: Date): number {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(target.getFullYear(), target.getMonth(), target.getDate());
  let count = 0;
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
  }
  return count;
}

/**
 * Expiry-urgency tier (V1 funcs_quote.pl:732-803): expiry on/before today →
 * 'red'; 1 business day out → 'orange'; 2 → 'yellow'; 3+ (or no date) → null.
 */
export function daysOutTier(expires: string, today: Date = new Date()): DaysOutTier {
  const exp = parseUsDate(expires);
  if (!exp) return null;
  const t0 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const e0 = new Date(exp.getFullYear(), exp.getMonth(), exp.getDate());
  if (e0 <= t0) return 'red';
  const biz = businessDaysUntil(e0, t0);
  if (biz === 1) return 'orange';
  if (biz === 2) return 'yellow';
  return null;
}

/** Text class for a days-out tier (used on the Expires cell). */
export function daysOutTierClass(tier: DaysOutTier): string {
  switch (tier) {
    case 'red':    return 'text-red-700 dark:text-red-400 font-semibold';
    case 'orange': return 'text-orange-600 dark:text-orange-400 font-semibold';
    case 'yellow': return 'text-yellow-700 dark:text-yellow-500 font-semibold';
    default:       return '';
  }
}

/**
 * Quote-status → badge color classes (bg/text/border). Single source of truth,
 * previously triplicated across QuoteLaneRow, LaneTable and QuoteDetailPage's
 * StatusPill. The only statuses are SECURED (green), UNSECURED (red) and EXPIRED
 * (greyed, End Date passed); an unmapped value falls back to UNSECURED.
 */
const STATUS_CLASS: Record<string, string> = {
  SECURED:   'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
  UNSECURED: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
  EXPIRED:   'bg-slate-100 text-slate-400 border-slate-200 dark:bg-slate-700 dark:text-slate-500 dark:border-slate-600',
};

/** Badge color classes for a quote status; `fallback` is the key used when the
 *  status is unmapped (default 'UNSECURED'). */
export function statusBadgeClass(status: string, fallback: keyof typeof STATUS_CLASS = 'UNSECURED'): string {
  return STATUS_CLASS[status] ?? STATUS_CLASS[fallback];
}
